import type { CanvasState } from '../store/types';

type RectLike = { left: number; top: number; width: number; height: number };

export interface OutlineOpts {
  /** Max nodes emitted per frame (largest by area). Default 12. */
  maxNodesPerFrame?: number;
  /** Rect provider — injectable for tests (jsdom does no layout). */
  rectOf?: (el: Element) => RectLike | undefined;
  /** Document to query — defaults to the global. */
  doc?: Document;
}

const HTML_KINDS = new Set(['free', 'main']);

/** Ids of the cf-id-editable html design frames (free/main). */
export function htmlFrameIds(state: CanvasState): string[] {
  return state.doc.frames.filter((f) => HTML_KINDS.has(f.kind)).map((f) => f.id);
}

/**
 * Build a compact node outline for the given free/main design frames (the
 * cf-id-editable kinds), read from the LIVE DOM ([data-cf-id] under
 * [data-frame-id]) so the agent's handles equal the ones layerSelect/LayerOverlay
 * use. Rects are frame-local CSS px (zoom-corrected). Frames with no mounted DOM
 * are omitted. Returns '' if nothing to show.
 */
export function buildNodeOutline(state: CanvasState, frameIds: string[], opts: OutlineOpts = {}): string {
  const doc = opts.doc ?? (typeof document !== 'undefined' ? document : undefined);
  if (!doc) return '';
  const cap = opts.maxNodesPerFrame ?? 12;
  const rectOf = opts.rectOf ?? ((el: Element) => el.getBoundingClientRect() as RectLike);
  const scale = state.doc.viewport.zoom || 1;
  const kindById = new Map(state.doc.frames.map((f) => [f.id, f.kind] as const));
  const lines: string[] = [];

  for (const fid of frameIds) {
    if (!HTML_KINDS.has(kindById.get(fid) ?? '')) continue;
    const root = doc.querySelector(`[data-frame-id="${fid}"]`);
    if (!root) continue; // unmounted → omit
    const fr = rectOf(root);
    if (!fr) continue;
    const nodes = Array.from(root.querySelectorAll('[data-cf-id]')) as Element[];
    const ranked = nodes
      .map((el) => { const r = rectOf(el); return r ? { el, r, area: r.width * r.height } : null; })
      .filter((x): x is { el: Element; r: RectLike; area: number } => !!x && x.area > 0)
      .sort((a, b) => b.area - a.area)
      .slice(0, cap);
    if (!ranked.length) continue;
    lines.push(`  nodes(${fid}):`);
    for (const { el, r } of ranked) {
      const x = Math.round((r.left - fr.left) / scale);
      const y = Math.round((r.top - fr.top) / scale);
      const w = Math.round(r.width / scale);
      const h = Math.round(r.height / scale);
      const cf = el.getAttribute('data-cf-id');
      const tag = el.tagName.toLowerCase();
      const text = (el.textContent ?? '').trim().replace(/\s+/g, ' ').slice(0, 40);
      lines.push(`    ${cf} ${tag} @${x},${y} ${w}×${h}${text ? ` "${text}"` : ''}`);
    }
  }
  return lines.join('\n');
}
